import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase, CellarTobacco, CellarPipe, CellarLighter } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import { compressImage, isImageFile, formatFileSize } from '../lib/imageCompression';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/ui/Button';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';

type ItemType = 'tobacco' | 'pipe' | 'lighter';

export default function EditCellarItem() {
  const navigate = useNavigate();
  const { type, id } = useParams<{ type: ItemType; id: string }>();
  const { currentUser } = useEmberStore();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);
  // Pipe cropper state (2:1 aspect)
  const [showPipeCropper, setShowPipeCropper] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [cropImgEl, setCropImgEl] = useState<HTMLImageElement | null>(null);
  const [cropScale, setCropScale] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{x:number;y:number}|null>(null);
  const [cropBaseScale, setCropBaseScale] = useState(1);
  const cropContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Tobacco states
  const [brand, setBrand] = useState('');
  const [blendName, setBlendName] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<'grams' | 'ounces'>('grams');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [tinnedDate, setTinnedDate] = useState('');
  const [status, setStatus] = useState<'sealed' | 'opened' | 'finished'>('sealed');
  const [storageType, setStorageType] = useState<'tin' | 'jar' | 'pouch' | 'bulk' | ''>('');
  const [notes, setNotes] = useState('');
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  
  // Pipe states
  const [maker, setMaker] = useState('');
  const [shape, setShape] = useState('');
  const [style, setStyle] = useState('');
  
  // Lighter/Matches states
  const [lighterBrand, setLighterBrand] = useState('');
  const [lighterStyle, setLighterStyle] = useState('');
  const [colour, setColour] = useState('');
  const [lighterType, setLighterType] = useState<'lighter' | 'matches'>('lighter');

  useEffect(() => {
    loadItem();
  }, [id, type]);

  useEffect(() => {
    // When crop modal opens and image/container are ready, compute the base scale
    if (showPipeCropper && cropImgEl && cropContainerRef.current) {
      const rect = cropContainerRef.current.getBoundingClientRect();
      const base = Math.max(rect.width / cropImgEl.width, rect.height / cropImgEl.height);
      setCropBaseScale(base);
      setCropScale(1);
      setCropOffset({ x: 0, y: 0 });
    }
  }, [showPipeCropper, cropImgEl]);

  async function loadItem() {
    if (!id || !type) return;

    setLoading(true);
    try {
      if (type === 'tobacco') {
        const { data, error } = await supabase
          .from('cellar_tobacco')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        if (data) {
          setBrand(data.brand);
          setBlendName(data.blend_name);
          setAmount(data.amount.toString());
          setUnit(data.unit);
          setPurchaseDate(data.purchase_date);
          setTinnedDate(data.tinned_date || '');
          setStatus(data.status || 'sealed');
          setStorageType(data.storage_type || '');
          setNotes(data.notes || '');
          setExistingImageUrl(data.image_url);
        }
      } else if (type === 'pipe') {
        const { data, error } = await supabase
          .from('cellar_pipes')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        if (data) {
          setMaker(data.maker);
          setShape(data.shape);
          setStyle(data.style || '');
          setPurchaseDate(data.purchase_date);
          setExistingImageUrl(data.image_url);
        }
      } else if (type === 'lighter') {
        const { data, error } = await supabase
          .from('cellar_lighters')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        if (data) {
          setLighterBrand(data.brand);
          setLighterStyle(data.style || '');
          setColour(data.colour || '');
          setLighterType(data.type);
          setPurchaseDate(data.purchase_date);
          setExistingImageUrl(data.image_url);
        }
      }
    } catch (err) {
      console.error('Error loading item:', err);
      toast.error('Failed to load item');
      navigate('/cellar');
    } finally {
      setLoading(false);
    }
  }

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }

    if (!isImageFile(file)) {
      toast.error('Please select a valid image file');
      return;
    }

    if (type === 'pipe') {
      setCompressing(true);
      try {
        // For pipes, we need to show the cropper first
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          setCropImageUrl(dataUrl);
          const img = new Image();
          img.onload = () => {
            setCropImgEl(img);
            setCropScale(1);
            setCropOffset({ x: 0, y: 0 });
            setShowPipeCropper(true);
            setCompressing(false);
          };
          img.onerror = () => {
            toast.error('Failed to load image for cropping');
            setCompressing(false);
          };
          img.src = dataUrl;
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error preparing pipe photo:', error);
        toast.error('Failed to process image');
        setCompressing(false);
      }
      return;
    }

    setCompressing(true);
    try {
      const compressed = await compressImage(file, {
        maxWidth: 800,
        maxHeight: 800,
        quality: 0.85,
        outputFormat: 'image/jpeg',
      });

      setPhotoFile(compressed);

      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(compressed);
    } catch (error) {
      console.error('Error compressing image:', error);
      toast.error('Failed to process image');
    } finally {
      setCompressing(false);
    }
  }

  async function uploadPhoto(): Promise<string | null> {
    if (!photoFile || !currentUser) return existingImageUrl;

    const filePath = `${currentUser.id}/${Date.now()}-${photoFile.name}`;
    const { data, error } = await supabase.storage
      .from('cellar-images')
      .upload(filePath, photoFile, { upsert: false });

    if (error || !data) {
      console.error('Error uploading photo:', error);
      return existingImageUrl;
    }

    const { data: pub } = supabase.storage
      .from('cellar-images')
      .getPublicUrl(data.path);

    return pub?.publicUrl ?? existingImageUrl;
  }

  async function handleSubmitTobacco(e: React.FormEvent) {
    e.preventDefault();
    if (!currentUser || !id) return;

    setSaving(true);
    try {
      const image_url = await uploadPhoto();

      const { error } = await supabase
        .from('cellar_tobacco')
        .update({
          brand: brand.trim(),
          blend_name: blendName.trim(),
          amount: parseFloat(amount),
          unit,
          purchase_date: purchaseDate,
          tinned_date: tinnedDate || null,
          status,
          storage_type: storageType || null,
          notes: notes.trim() || null,
          image_url,
        })
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Tobacco updated successfully!');
      navigate('/cellar');
    } catch (err) {
      console.error('Error updating tobacco:', err);
      toast.error('Failed to update tobacco');
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitPipe(e: React.FormEvent) {
    e.preventDefault();
    if (!currentUser || !id) return;

    setSaving(true);
    try {
      const image_url = await uploadPhoto();

      const { error } = await supabase
        .from('cellar_pipes')
        .update({
          maker: maker.trim(),
          shape: shape.trim(),
          style: style.trim() || null,
          purchase_date: purchaseDate,
          image_url,
        })
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Pipe updated successfully!');
      navigate('/cellar');
    } catch (err) {
      console.error('Error updating pipe:', err);
      toast.error('Failed to update pipe');
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitLighter(e: React.FormEvent) {
    e.preventDefault();
    if (!currentUser || !id) return;

    setSaving(true);
    try {
      const image_url = await uploadPhoto();

      const { error } = await supabase
        .from('cellar_lighters')
        .update({
          type: lighterType,
          brand: lighterBrand.trim(),
          style: lighterStyle.trim() || null,
          colour: colour.trim() || null,
          purchase_date: purchaseDate,
          image_url,
        })
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Item updated successfully!');
      navigate('/cellar');
    } catch (err) {
      console.error('Error updating lighter:', err);
      toast.error('Failed to update item');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingSpinner fullPage text="Loading item..." />;
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '120px' }}>
      {/* Pipe Cropper Modal (2:1) */}
      {showPipeCropper && cropImgEl && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '12px', width: '100%', maxWidth: '640px', padding: '1rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-accent)' }}>Position Your Pipe</h3>
            <p style={{ fontSize: '0.875rem', color: '#999', marginBottom: '0.75rem' }}>Pan and zoom so the pipe fits the frame, then tap Use Photo.</p>

            {/* Crop viewport */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                paddingTop: '50%', // 2:1 aspect (height = width*0.5)
                backgroundColor: '#000',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}
              ref={cropContainerRef}
              onMouseDown={(e) => { setIsPanning(true); setPanStart({ x: e.clientX, y: e.clientY }); }}
              onMouseMove={(e) => {
                if (!isPanning) return;
                if (!panStart) return;
                const dx = e.clientX - panStart.x;
                const dy = e.clientY - panStart.y;
                setPanStart({ x: e.clientX, y: e.clientY });
                setCropOffset(({ x, y }) => ({ x: x + dx, y: y + dy }));
              }}
              onMouseUp={() => setIsPanning(false)}
              onMouseLeave={() => setIsPanning(false)}
              onTouchStart={(e) => { const t = e.touches[0]; setIsPanning(true); setPanStart({ x: t.clientX, y: t.clientY }); }}
              onTouchMove={(e) => {
                if (!isPanning) return;
                const t = e.touches[0];
                if (!panStart) return;
                const dx = t.clientX - panStart.x;
                const dy = t.clientY - panStart.y;
                setPanStart({ x: t.clientX, y: t.clientY });
                setCropOffset(({ x, y }) => ({ x: x + dx, y: y + dy }));
              }}
              onTouchEnd={() => setIsPanning(false)}
            >
              {/* Image element transformed to simulate crop */}
              <img
                src={cropImageUrl || ''}
                alt="Crop"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) translate(${cropOffset.x}px, ${cropOffset.y}px) scale(${cropBaseScale * cropScale})`,
                  userSelect: 'none',
                  touchAction: 'none',
                  maxWidth: 'none',
                  maxHeight: 'none'
                }}
                draggable={false}
              />
              {/* 2:1 mask border */}
              <div style={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 2px rgba(196,255,0,0.6)', pointerEvents: 'none', borderRadius: '8px' }} />
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
              <span style={{ color: '#999', fontSize: '0.875rem' }}>Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={cropScale}
                onChange={(e) => setCropScale(parseFloat(e.target.value))}
                style={{ flex: 1 }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowPipeCropper(false); setCropImageUrl(null); setCropImgEl(null); }}
                style={{ padding: '0.6rem 1rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Render cropped area to canvas (export 1000x500)
                  if (!cropImgEl) return;
                  const targetW = 1000; const targetH = 500; // 2:1
                  const canvas = document.createElement('canvas');
                  canvas.width = targetW; canvas.height = targetH;
                  const ctx = canvas.getContext('2d');
                  if (!ctx) return;

                  // Compute draw parameters based on current transform
                  // Use container dimensions to map offsets consistently
                  const rect = cropContainerRef.current?.getBoundingClientRect();
                  const contW = rect?.width || targetW;
                  const contH = rect?.height || targetH;
                  const baseScale = Math.max(targetW / cropImgEl.width, targetH / cropImgEl.height);
                  const scale = baseScale * cropScale;
                  const drawW = cropImgEl.width * scale;
                  const drawH = cropImgEl.height * scale;
                  const dx = targetW / 2 - drawW / 2 + cropOffset.x * (targetW / contW);
                  const dy = targetH / 2 - drawH / 2 + cropOffset.y * (targetH / contH);

                  ctx.fillStyle = '#0b0b0b';
                  ctx.fillRect(0, 0, targetW, targetH);
                  ctx.imageSmoothingQuality = 'high';
                  ctx.drawImage(cropImgEl, dx, dy, drawW, drawH);

                  const blob: Blob | null = await new Promise((res) => canvas.toBlob((b) => res(b), 'image/jpeg', 0.92));
                  if (blob) {
                    const file = new File([blob], 'pipe-crop.jpg', { type: 'image/jpeg' });
                    // Compress lightly to keep consistency with pipeline (but maintain 2:1 bounds)
                    setCompressing(true);
                    try {
                      const compressed = await compressImage(file, {
                        maxWidth: 1000,
                        maxHeight: 500,
                        quality: 0.9,
                        outputFormat: 'image/jpeg',
                      });
                      setPhotoFile(compressed);
                      const r = new FileReader();
                      r.onload = (ev) => {
                        const result = ev.target?.result as string;
                        if (typeof result === 'string') {
                          setPhotoPreview(result);
                        }
                      };
                      r.readAsDataURL(compressed);
                    } catch (err) {
                      console.error('Pipe crop compress error:', err);
                      toast.error('Failed to process photo');
                    } finally {
                      setCompressing(false);
                    }
                  }
                  setShowPipeCropper(false);
                  setCropImageUrl(null);
                  setCropImgEl(null);
                }}
                style={{ padding: '0.6rem 1rem', backgroundColor: 'var(--color-accent)', border: 'none', color: '#0b0b0b', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }}
              >
                Use Photo
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
      }}>
        <button
          onClick={() => navigate('/cellar')}
          style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }}
        >
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ fontSize: '1.5rem', fontFamily: '"Oswald", sans-serif', letterSpacing: '-0.02em' }}>
          Edit {type === 'tobacco' ? 'Tobacco' : type === 'pipe' ? 'Pipe' : 'Lighter/Matches'}
        </h1>
      </header>

      <div className="container" style={{ paddingTop: '1rem' }}>
        {type === 'tobacco' ? (
          // Tobacco Edit Form
          <form onSubmit={handleSubmitTobacco} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Blender *
              </label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="e.g., Cornell & Diehl"
                required
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Blend Name *
              </label>
              <input
                type="text"
                value={blendName}
                onChange={(e) => setBlendName(e.target.value)}
                placeholder="e.g., Opening Night"
                required
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Amount *
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="50"
                  required
                  step="0.01"
                  min="0"
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Unit *
                </label>
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value as 'grams' | 'ounces')}
                  required
                  style={{ width: '100%' }}
                >
                  <option value="grams">Grams</option>
                  <option value="ounces">Ounces</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Purchase Date *
              </label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Tinned Date (Optional)
              </label>
              <input
                type="date"
                value={tinnedDate}
                onChange={(e) => setTinnedDate(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Storage Type (Optional)
              </label>
              <select
                value={storageType}
                onChange={(e) => setStorageType(e.target.value as any)}
                style={{ width: '100%' }}
              >
                <option value="">None</option>
                <option value="tin">🥫 Tin</option>
                <option value="jar">🫙 Jar</option>
                <option value="pouch">📦 Pouch</option>
                <option value="bulk">📦 Bulk</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '500' }}>
                Status *
              </label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <label style={{ 
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid',
                  borderColor: status === 'sealed' ? 'var(--color-success)' : 'var(--color-border)',
                  backgroundColor: status === 'sealed' ? 'rgba(107, 175, 74, 0.1)' : 'var(--color-card)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                }}>
                  <input
                    type="radio"
                    name="status"
                    value="sealed"
                    checked={status === 'sealed'}
                    onChange={(e) => setStatus(e.target.value as any)}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '0.875rem', fontWeight: status === 'sealed' ? '600' : '400' }}>
                    🟢 Sealed
                  </div>
                </label>

                <label style={{ 
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid',
                  borderColor: status === 'opened' ? '#facc15' : 'var(--color-border)',
                  backgroundColor: status === 'opened' ? 'rgba(250, 204, 21, 0.1)' : 'var(--color-card)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                }}>
                  <input
                    type="radio"
                    name="status"
                    value="opened"
                    checked={status === 'opened'}
                    onChange={(e) => setStatus(e.target.value as any)}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '0.875rem', fontWeight: status === 'opened' ? '600' : '400' }}>
                    🟡 Opened
                  </div>
                </label>

                <label style={{ 
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid',
                  borderColor: status === 'finished' ? '#6b7280' : 'var(--color-border)',
                  backgroundColor: status === 'finished' ? 'rgba(107, 114, 128, 0.1)' : 'var(--color-card)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                }}>
                  <input
                    type="radio"
                    name="status"
                    value="finished"
                    checked={status === 'finished'}
                    onChange={(e) => setStatus(e.target.value as any)}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '0.875rem', fontWeight: status === 'finished' ? '600' : '400' }}>
                    ⚪ Finished
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Tasting notes, storage location, etc."
                rows={3}
                style={{ width: '100%' }}
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Photo {existingImageUrl && '(Upload new to replace)'}
              </label>
              
              {/* Current or New Preview */}
              {existingImageUrl && !photoPreview && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <img
                    src={existingImageUrl}
                    alt="Current"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: '#1f1f1f',
                    }}
                  />
                </div>
              )}

              {photoPreview && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <img
                    src={photoPreview}
                    alt="New Preview"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: '#1f1f1f',
                    }}
                  />
                  {photoFile && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#999' }}>
                      {photoFile.name} ({formatFileSize(photoFile.size)})
                    </div>
                  )}
                </div>
              )}

              {compressing && (
                <div style={{ 
                  padding: '2rem', 
                  textAlign: 'center',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  marginBottom: '0.75rem'
                }}>
                  <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '32px', height: '32px' }}></div>
                  <span style={{ color: '#999' }}>Compressing image...</span>
                </div>
              )}

              {/* Upload Buttons */}
              {!compressing && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <label
                    htmlFor="photo-gallery"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
                  </label>

                  <label
                    htmlFor="photo-camera"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
                  </label>
                </div>
              )}

              <input
                id="photo-gallery"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />

              <input
                id="photo-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              loading={saving || compressing}
              fullWidth
            >
              Update Tobacco
            </Button>
          </form>
        ) : type === 'pipe' ? (
          // Pipe Edit Form
          <form onSubmit={handleSubmitPipe} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Maker *
              </label>
              <input
                type="text"
                value={maker}
                onChange={(e) => setMaker(e.target.value)}
                placeholder="e.g., Peterson"
                required
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Shape *
              </label>
              <input
                type="text"
                value={shape}
                onChange={(e) => setShape(e.target.value)}
                placeholder="e.g., Billiard"
                required
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Style (Optional)
              </label>
              <input
                type="text"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="e.g., Rusticated"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Purchase Date *
              </label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
                style={{ width: '100%' }}
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Photo {existingImageUrl && '(Upload new to replace)'}
              </label>
              
              {/* Current or New Preview */}
              {existingImageUrl && !photoPreview && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <img
                    src={existingImageUrl}
                    alt="Current"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: '#1f1f1f',
                    }}
                  />
                </div>
              )}

              {photoPreview && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <img
                    src={photoPreview}
                    alt="New Preview"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: '#1f1f1f',
                    }}
                  />
                  {photoFile && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#999' }}>
                      {photoFile.name} ({formatFileSize(photoFile.size)})
                    </div>
                  )}
                </div>
              )}

              {compressing && (
                <div style={{ 
                  padding: '2rem', 
                  textAlign: 'center',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  marginBottom: '0.75rem'
                }}>
                  <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '32px', height: '32px' }}></div>
                  <span style={{ color: '#999' }}>Compressing image...</span>
                </div>
              )}

              {/* Upload Buttons */}
              {!compressing && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <label
                    htmlFor="photo-gallery"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
                  </label>

                  <label
                    htmlFor="photo-camera"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
                  </label>
                </div>
              )}

              <input
                id="photo-gallery"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />

              <input
                id="photo-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              loading={saving || compressing}
              fullWidth
            >
              Update Pipe
            </Button>
          </form>
        ) : type === 'lighter' ? (
          // Lighter/Matches Edit Form
          <form onSubmit={handleSubmitLighter} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Blender *
              </label>
              <input
                type="text"
                value={lighterBrand}
                onChange={(e) => setLighterBrand(e.target.value)}
                placeholder="e.g., Zippo"
                required
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Style (Optional)
              </label>
              <input
                type="text"
                value={lighterStyle}
                onChange={(e) => setLighterStyle(e.target.value)}
                placeholder="e.g., Pipe Lighter"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Colour (Optional)
              </label>
              <input
                type="text"
                value={colour}
                onChange={(e) => setColour(e.target.value)}
                placeholder="e.g., Brass"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Purchase Date *
              </label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
                style={{ width: '100%' }}
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Photo {existingImageUrl && '(Upload new to replace)'}
              </label>
              
              {/* Current or New Preview */}
              {existingImageUrl && !photoPreview && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <img
                    src={existingImageUrl}
                    alt="Current"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: '#1f1f1f',
                    }}
                  />
                </div>
              )}

              {photoPreview && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <img
                    src={photoPreview}
                    alt="New Preview"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: '#1f1f1f',
                    }}
                  />
                  {photoFile && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#999' }}>
                      {photoFile.name} ({formatFileSize(photoFile.size)})
                    </div>
                  )}
                </div>
              )}

              {compressing && (
                <div style={{ 
                  padding: '2rem', 
                  textAlign: 'center',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  marginBottom: '0.75rem'
                }}>
                  <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '32px', height: '32px' }}></div>
                  <span style={{ color: '#999' }}>Compressing image...</span>
                </div>
              )}

              {/* Upload Buttons */}
              {!compressing && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <label
                    htmlFor="photo-gallery"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
                  </label>

                  <label
                    htmlFor="photo-camera"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
                  </label>
                </div>
              )}

              <input
                id="photo-gallery"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />

              <input
                id="photo-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              loading={saving || compressing}
              fullWidth
            >
              Update {lighterType === 'lighter' ? 'Lighter' : 'Matches'}
            </Button>
          </form>
        ) : null}
      </div>

      <BottomNav />
    </div>
  );
}

